/**
 * FxPopover — lightweight popover showing calculated field details.
 *
 * Triggered by hovering or clicking the `fx` badge on a FieldListItem.
 * Shows: label → description (plain English) → formula (SQL expression).
 */
import { useState, useCallback, useEffect, useRef } from "react";
import {
  makeStyles,
  tokens,
  Popover,
  PopoverSurface,
  PopoverTrigger,
} from "@fluentui/react-components";

const useStyles = makeStyles({
  trigger: {
    cursor: "pointer",
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusSmall,
    padding: "0 4px",
    lineHeight: "16px",
    flexShrink: 0,
    border: "none",
  },
  surface: {
    padding: "12px 14px",
    maxWidth: "320px",
    fontSize: tokens.fontSizeBase200,
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    marginBottom: "4px",
    color: tokens.colorNeutralForeground1,
  },
  sectionLabel: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    marginTop: "6px",
    marginBottom: "2px",
  },
  descriptionText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    lineHeight: "18px",
  },
  formulaText: {
    fontFamily: "monospace",
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground3,
    padding: "4px 6px",
    borderRadius: tokens.borderRadiusSmall,
    wordBreak: "break-all" as const,
    whiteSpace: "pre-wrap" as const,
  },
});

interface FxPopoverProps {
  label: string;
  formula?: string;
  formulaDescription?: string;
}

export default function FxPopover({
  label,
  formula,
  formulaDescription,
}: FxPopoverProps) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleMouseEnter = useCallback(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setOpen(true), 300);
  }, []);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setOpen(false), 200);
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
  }, []);

  // Clear pending timeouts on unmount to avoid state updates after unmount
  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const hasFormula = !!formula;
  const hasDescription = !!formulaDescription;

  return (
    <Popover
      open={open}
      onOpenChange={(_, data) => setOpen(data.open)}
      positioning="above-end"
      trapFocus={false}
    >
      <PopoverTrigger>
        <button
          type="button"
          className={styles.trigger}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
          tabIndex={0}
        >
          fx
        </button>
      </PopoverTrigger>
      <PopoverSurface
        className={styles.surface}
        onMouseEnter={() => clearTimeout(timeoutRef.current)}
        onMouseLeave={handleMouseLeave}
      >
        <div className={styles.title}>{label}</div>
        {hasDescription && (
          <div className={styles.descriptionText}>{formulaDescription}</div>
        )}
        {hasFormula ? (
          <div className={styles.formulaText} style={hasDescription ? { marginTop: "6px" } : undefined}>{formula}</div>
        ) : !hasDescription ? (
          <div className={styles.formulaText}>Calculated field</div>
        ) : null}
      </PopoverSurface>
    </Popover>
  );
}
